import React, { useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import Header from "../../../components/Header";
import { useGetPermissionsQuery } from "../../../state/api";
import { useDispatch } from "react-redux";
import { addRoleAction } from "../../../redux/actions/roleAction";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";

const validationSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  isActive: yup.string().required("Select status"),
  category: yup.string().required("Category is required to create a role"),
  selectPermissions: yup.array().required("Select a permissions"),
});

const CreateRole = () => {
  const initialFormState = {
    title: "",
    category: "",
    isActive: "",
    selectPermissions: [],
  };
  const [formData, setFormData] = useState(initialFormState);

  const {
    data: permissions = [],
    isLoading,
    isError,
  } = useGetPermissionsQuery();

  const [err, setErr] = useState({});
  const [checkedItems, setCheckedItems] = useState([]);

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    try {
      await validationSchema.validateAt(fieldName, formData);
      setErr({ ...err, [fieldName]: "" });
    } catch (error) {
      setErr({ ...err, [fieldName]: error.message });
    }
  };

  const handleCheckboxChange = (event) => {
    const itemId = event.target.name;
    const isChecked = event.target.checked;

    setCheckedItems((prevCheckedItems) => ({
      ...prevCheckedItems,
      [itemId]: isChecked,
    }));
  };

  const handleSelectAll = (category, checked) => {
    const updatedCheckedItems = { ...checkedItems };

    permissions.forEach((permission) => {
      if (permission.table === category) {
        updatedCheckedItems[permission._id] = checked;
      }
    });

    setCheckedItems(updatedCheckedItems);
  };

  const groupedPermissions = permissions.reduce((groups, permission) => {
    const table = permission.table;

    if (!groups[table]) {
      groups[table] = [];
    }

    groups[table].push(permission);
    return groups;
  }, {});

  const groupsArray = Object.entries(groupedPermissions);
  const itemsPerRow = 5;

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await validationSchema.validate(
        { ...formData, table: formData.table },
        { abortEarly: false }
      );

      const selectedPermissions = Object.entries(checkedItems)
        .filter(([_, isChecked]) => isChecked)
        .map(([permissionId, _]) => permissionId);

      const updatedFormData = {
        ...formData,
        selectPermissions: selectedPermissions,
      };

      // console.log(updatedFormData);

      const response = await dispatch(addRoleAction(updatedFormData));
      // console.log("Data submitted:", response.data);
      if (response) {
        toast.success("Role added successfully");
        navigate("/role");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error("Failed to add role");
      }
    } catch (error) {
      if (error.name === "ValidationError") {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErr(validationErrors);
      } else {
        console.error("Error submitting data:", error);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <Box mt="1.5rem" mb="2.5rem" ml="2.5rem" mr="2.5rem">
      <Header title="ADD ROLE" />
      <form onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal">
          {/* TITLE */}
          <TextField
            label="Title"
            name="title"
            value={formData.title}
            onBlur={handleBlur}
            onChange={handleChange}
            margin="normal"
          />
          {err.title && (
            <Grid item>
              <Typography color="error">{err.title}</Typography>
            </Grid>
          )}{" "}
          {/* CATEGORY */}
          <TextField
            label="Category"
            name="category"
            value={formData.category}
            onBlur={handleBlur}
            onChange={handleChange}
            margin="normal"
          />
          {err.category && (
            <Grid item>
              <Typography color="error">{err.category}</Typography>
            </Grid>
          )}{" "}
          {/* IS ACTIVE */}
          <FormControl fullWidth variant="outlined" margin="normal">
            <InputLabel>Is Active</InputLabel>
            <Select
              name="isActive"
              value={formData.isActive}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              {/* <MenuItem value="">
                <em>Select</em>
              </MenuItem> */}
              <MenuItem value="true">True</MenuItem>
              <MenuItem value="false">False</MenuItem>
            </Select>
            {err.isActive && (
              <Grid item>
                <Typography color="error">{err.isActive}</Typography>
              </Grid>
            )}{" "}
          </FormControl>
          {/* PERMISSION */}
          <h3>Select Permissions</h3>
          <div>
            {groupsArray
              .map(([table, permissionsInTable], index) => (
                <Box
                  key={table}
                  // border="1px solid #ccc"
                  padding="10px"
                  marginBottom="20px"
                  // marginRight="10px"
                  style={{ width: `${100 / itemsPerRow}%` }}
                >
                  <h3>{table}</h3>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          permissionsInTable.every(
                            (permission) =>
                              checkedItems[permission._id] || false
                          ) && permissionsInTable.length > 0
                        }
                        onChange={(event) =>
                          handleSelectAll(table, event.target.checked)
                        }
                      />
                    }
                    label="Select All"
                  />
                  <FormGroup>
                    {permissionsInTable.map((permission) => (
                      <FormControlLabel
                        key={permission._id}
                        control={
                          <Checkbox
                            checked={checkedItems[permission._id] || false}
                            onBlur={handleBlur}
                            onChange={handleCheckboxChange}
                            name={permission._id}
                          />
                        }
                        label={permission.title}
                      />
                    ))}
                  </FormGroup>
                </Box>
              ))
              .reduce((rows, element, index) => {
                if (index % itemsPerRow === 0) rows.push([]);
                rows[rows.length - 1].push(element);
                return rows;
              }, [])
              .map((row, index) => (
                <Box key={index} display="flex" flexDirection="row">
                  {row}
                </Box>
              ))}
            {err.selectPermissions && (
              <Grid item>
                <Typography color="error">{err.selectPermissions}</Typography>
              </Grid>
            )}{" "}
          </div>
          {/* SUBMIT BUTTON */}
          <Grid container justifyContent="center" alignItems="center">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
            >
              Submit
            </Button>
          </Grid>
        </FormControl>
      </form>
    </Box>
  );
};

export default CreateRole;
